import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Disc3, Trash2, ExternalLink, Search, RefreshCw, ArrowLeft, Lock, Crown, Headphones, Music, FileText, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";

// Use current origin for API calls so it works on both preview and custom domain
const API = `${window.location.origin}/api`;

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: "easeOut" }
};

// Status badge component
const StatusBadge = ({ status, type = "submission" }) => {
  const statusColors = {
    pending: "bg-zinc-700 text-zinc-300",
    reviewing: "bg-yellow-900/50 text-yellow-400 border border-yellow-800",
    selected: "bg-green-900/50 text-neon border border-green-800",
    rejected: "bg-red-900/50 text-red-400 border border-red-800",
    approved: "bg-green-900/50 text-neon border border-green-800"
  };

  return (
    <span className={`px-3 py-1 text-xs uppercase tracking-wider font-bold ${statusColors[status] || statusColors.pending}`}>
      {status}
    </span>
  );
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg("");
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        localStorage.setItem('tpc_admin_auth', password);
        onLogin(true);
        toast.success("Access granted");
      }
    } catch (error) {
      console.error("Login error:", error);
      const msg = error.response?.data?.detail || error.message || "Login failed";
      setErrorMsg(msg);
      toast.error("Invalid password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-[#09090b] min-h-screen flex items-center justify-center">
      <div className="fixed inset-0 z-[9999] pointer-events-none opacity-[0.03] mix-blend-overlay bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
      
      <motion.div 
        {...fadeUp}
        className="relative z-10 w-full max-w-md px-6"
      >
        <div className="border border-zinc-800 bg-zinc-900/30 p-8">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Lock className="w-8 h-8 text-neon" />
            <span className="font-heading text-2xl uppercase tracking-wider text-white">Admin Access</span>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                Password
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="bg-transparent border border-zinc-800 focus:border-neon rounded-none px-4 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                data-testid="admin-password-input"
              />
              {errorMsg && (
                <p className="text-red-400 text-sm mt-2">{errorMsg}</p>
              )}
            </div>
            
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-6 py-4 transition-colors duration-300 border border-neon hover:border-white"
              data-testid="admin-login-btn"
            >
              {isLoading ? "Verifying..." : "Access Dashboard"}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <Link to="/" className="text-zinc-500 hover:text-neon text-sm transition-colors duration-300">
              ← Back to site
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState("submissions");
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [djApplications, setDjApplications] = useState([]);
  const [filteredDjApplications, setFilteredDjApplications] = useState([]);
  const [publishingSubs, setPublishingSubs] = useState([]);
  const [filteredPublishingSubs, setFilteredPublishingSubs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Check for saved auth on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem('tpc_admin_auth');
    if (savedAuth) {
      // Verify the saved password
      axios.post(`${API}/admin/login`, { password: savedAuth })
        .then(response => {
          if (response.data.success) {
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('tpc_admin_auth');
          }
        })
        .catch(() => {
          localStorage.removeItem('tpc_admin_auth');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  // Fetch submissions
  const fetchSubmissions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/submissions`);
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setSubmissions(sorted);
      setFilteredSubmissions(sorted);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      toast.error("Failed to load submissions");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch DJ applications
  const fetchDjApplications = async () => {
    try {
      const response = await axios.get(`${API}/dj-applications`);
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setDjApplications(sorted);
      setFilteredDjApplications(sorted);
    } catch (error) {
      console.error("Error fetching DJ applications:", error);
    }
  };

  // Fetch publishing submissions
  const fetchPublishingSubs = async () => {
    try {
      const response = await axios.get(`${API}/publishing/submissions`);
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setPublishingSubs(sorted);
      setFilteredPublishingSubs(sorted);
    } catch (error) {
      console.error("Error fetching publishing submissions:", error);
    }
  };

  // Fetch all data
  const fetchAllData = async () => {
    setIsLoading(true);
    await Promise.all([fetchSubmissions(), fetchDjApplications(), fetchPublishingSubs()]);
    setIsLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  // Filter submissions
  useEffect(() => {
    let filtered = submissions;

    // Search filter
    if (searchTerm && activeTab === "submissions") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(sub => 
        sub.artist_name.toLowerCase().includes(term) ||
        sub.track_title.toLowerCase().includes(term) ||
        sub.email.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }

    setFilteredSubmissions(filtered);
  }, [searchTerm, statusFilter, submissions, activeTab]);

  // Filter DJ applications
  useEffect(() => {
    let filtered = djApplications;

    // Search filter
    if (searchTerm && activeTab === "dj-list") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app => 
        app.dj_name.toLowerCase().includes(term) ||
        app.full_name.toLowerCase().includes(term) ||
        app.email.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    setFilteredDjApplications(filtered);
  }, [searchTerm, statusFilter, djApplications, activeTab]);

  // Filter publishing submissions
  useEffect(() => {
    let filtered = publishingSubs;
    if (searchTerm && activeTab === "publishing") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(sub =>
        sub.artist_name.toLowerCase().includes(term) ||
        sub.full_name.toLowerCase().includes(term) ||
        sub.email.toLowerCase().includes(term)
      );
    }
    if (statusFilter !== "all") {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }
    setFilteredPublishingSubs(filtered);
  }, [searchTerm, statusFilter, publishingSubs, activeTab]);

  // Update status
  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/submissions/${id}/status?status=${newStatus}`);
      setSubmissions(prev => 
        prev.map(sub => sub.id === id ? { ...sub, status: newStatus } : sub)
      );
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  // Update DJ application status
  const updateDjStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/dj-applications/${id}/status?status=${newStatus}`);
      setDjApplications(prev => 
        prev.map(app => app.id === id ? { ...app, status: newStatus } : app)
      );
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  // Delete submission
  const deleteSubmission = async (id) => {
    if (!window.confirm("Are you sure you want to delete this submission?")) return;
    
    try {
      await axios.delete(`${API}/submissions/${id}`);
      setSubmissions(prev => prev.filter(sub => sub.id !== id));
      toast.success("Submission deleted");
    } catch (error) {
      console.error("Error deleting submission:", error);
      toast.error("Failed to delete submission");
    }
  };

  // Delete DJ application
  const deleteDjApplication = async (id) => {
    if (!window.confirm("Are you sure you want to delete this application?")) return;
    
    try {
      await axios.delete(`${API}/dj-applications/${id}`);
      setDjApplications(prev => prev.filter(app => app.id !== id));
      toast.success("Application deleted");
    } catch (error) {
      console.error("Error deleting application:", error);
      toast.error("Failed to delete application");
    }
  };

  // Update publishing submission status
  const updatePublishingStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/publishing/submissions/${id}/status?status=${newStatus}`);
      setPublishingSubs(prev =>
        prev.map(sub => sub.id === id ? { ...sub, status: newStatus } : sub)
      );
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error("Error updating publishing status:", error);
      toast.error("Failed to update status");
    }
  };

  // Delete publishing submission
  const deletePublishingSub = async (id) => {
    if (!window.confirm("Are you sure you want to delete this publishing submission?")) return;
    try {
      await axios.delete(`${API}/publishing/submissions/${id}`);
      setPublishingSubs(prev => prev.filter(sub => sub.id !== id));
      toast.success("Publishing submission deleted");
    } catch (error) {
      console.error("Error deleting publishing submission:", error);
      toast.error("Failed to delete submission");
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('tpc_admin_auth');
    setIsAuthenticated(false);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show login if not authenticated
  if (!isAuthenticated && !isLoading) {
    return <AdminLogin onLogin={setIsAuthenticated} />;
  }

  // Show loading
  if (isLoading && !isAuthenticated) {
    return (
      <div className="bg-[#09090b] min-h-screen flex items-center justify-center">
        <Disc3 className="w-12 h-12 text-neon animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#09090b] min-h-screen text-white">
      {/* Noise overlay */}
      <div className="fixed inset-0 z-[9999] pointer-events-none opacity-[0.03] mix-blend-overlay bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
      
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-3 group" data-testid="admin-logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/dfa6la5v_TPRESSLOGOSQ.png" 
                alt="Test Press Club" 
                className="h-12 w-auto"
              />
              <span className="font-heading text-sm uppercase tracking-wider text-zinc-500">Admin</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-zinc-500 hover:text-black transition-colors duration-300 text-sm"
              data-testid="back-to-site-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to site
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={fetchAllData}
              className="bg-transparent text-zinc-400 hover:text-neon border border-zinc-800 hover:border-neon rounded-none px-4 py-2 transition-colors duration-300"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-transparent text-zinc-500 hover:text-red-400 border border-zinc-800 hover:border-red-800 rounded-none px-4 py-2 transition-colors duration-300"
              data-testid="logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-8">
        <motion.div {...fadeUp}>
          {/* Tabs */}
          <div className="flex gap-4 mb-8 border-b border-zinc-800">
            <button
              onClick={() => { setActiveTab("submissions"); setSearchTerm(""); setStatusFilter("all"); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm uppercase tracking-wider font-bold transition-colors duration-300 border-b-2 -mb-px ${
                activeTab === "submissions" 
                  ? "text-neon border-neon" 
                  : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}
              data-testid="tab-submissions"
            >
              <Music className="w-4 h-4" />
              Track Submissions ({submissions.length})
            </button>
            <button
              onClick={() => { setActiveTab("dj-list"); setSearchTerm(""); setStatusFilter("all"); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm uppercase tracking-wider font-bold transition-colors duration-300 border-b-2 -mb-px ${
                activeTab === "dj-list" 
                  ? "text-neon border-neon" 
                  : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}
              data-testid="tab-dj-list"
            >
              <Headphones className="w-4 h-4" />
              DJ List Applications ({djApplications.length})
            </button>
            <button
              onClick={() => { setActiveTab("publishing"); setSearchTerm(""); setStatusFilter("all"); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm uppercase tracking-wider font-bold transition-colors duration-300 border-b-2 -mb-px ${
                activeTab === "publishing" 
                  ? "text-neon border-neon" 
                  : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}
              data-testid="tab-publishing"
            >
              <FileText className="w-4 h-4" />
              Publishing ({publishingSubs.length})
            </button>
          </div>

          {/* Title and Stats */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Dashboard</p>
              <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight">
                {activeTab === "submissions" ? "Submissions" : activeTab === "dj-list" ? "DJ List Applications" : "Publishing Submissions"}
              </h1>
            </div>
            {activeTab === "submissions" ? (
              <div className="flex gap-6 text-sm">
              <div className="text-zinc-500">
                Total: <span className="text-white font-bold">{submissions.length}</span>
              </div>
              <div className="text-zinc-500">
                Pro: <span className="text-neon font-bold">{submissions.filter(s => s.is_pro).length}</span>
              </div>
              <div className="text-zinc-500">
                Pending: <span className="text-yellow-400 font-bold">{submissions.filter(s => s.status === 'pending').length}</span>
              </div>
              <div className="text-zinc-500">
                Selected: <span className="text-green-400 font-bold">{submissions.filter(s => s.status === 'selected').length}</span>
              </div>
            </div>
            ) : activeTab === "dj-list" ? (
              <div className="flex gap-6 text-sm">
                <div className="text-zinc-500">
                  Total: <span className="text-white font-bold">{djApplications.length}</span>
                </div>
                <div className="text-zinc-500">
                  Pending: <span className="text-yellow-400 font-bold">{djApplications.filter(a => a.status === 'pending').length}</span>
                </div>
                <div className="text-zinc-500">
                  Approved: <span className="text-neon font-bold">{djApplications.filter(a => a.status === 'approved').length}</span>
                </div>
              </div>
            ) : (
              <div className="flex gap-6 text-sm">
                <div className="text-zinc-500">
                  Total: <span className="text-white font-bold">{publishingSubs.length}</span>
                </div>
                <div className="text-zinc-500">
                  Pending: <span className="text-yellow-400 font-bold">{publishingSubs.filter(s => s.status === 'pending').length}</span>
                </div>
                <div className="text-zinc-500">
                  Contacted: <span className="text-neon font-bold">{publishingSubs.filter(s => s.status === 'contacted').length}</span>
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
              <Input
                type="text"
                placeholder={activeTab === "submissions" ? "Search by artist, track, or email..." : activeTab === "dj-list" ? "Search by DJ name or email..." : "Search by artist name or email..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent border border-zinc-800 focus:border-neon rounded-none pl-10 py-3 text-sm placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-transparent border border-zinc-800 rounded-none focus:ring-0 focus:ring-offset-0" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                <SelectItem value="all" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">All Status</SelectItem>
                <SelectItem value="pending" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Pending</SelectItem>
                {activeTab === "submissions" ? (
                  <>
                    <SelectItem value="reviewing" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Reviewing</SelectItem>
                    <SelectItem value="selected" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Selected</SelectItem>
                  </>
                ) : activeTab === "dj-list" ? (
                  <SelectItem value="approved" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Approved</SelectItem>
                ) : (
                  <>
                    <SelectItem value="reviewing" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Reviewing</SelectItem>
                    <SelectItem value="contacted" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Contacted</SelectItem>
                  </>
                )}
                <SelectItem value="rejected" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Content based on active tab */}
          {activeTab === "submissions" ? (
            /* Submissions Table */
            <>
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Disc3 className="w-12 h-12 text-neon animate-spin" />
                </div>
              ) : filteredSubmissions.length === 0 ? (
                <div className="text-center py-20 border border-zinc-800">
                  <p className="text-zinc-500 text-lg">No submissions found</p>
                </div>
              ) : (
                <div className="border border-zinc-800 overflow-x-auto">
                  <table className="w-full text-sm" data-testid="submissions-table">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-zinc-900/50">
                        <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Type</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Artist</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Track</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Email</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Listeners</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Links</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Status</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Date</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map((submission, index) => (
                    <tr 
                      key={submission.id} 
                      className="border-b border-zinc-800/50 hover:bg-zinc-900/30 transition-colors duration-200"
                      data-testid={`submission-row-${index}`}
                    >
                      <td className="p-4">
                        {submission.is_pro ? (
                          <span className="flex items-center gap-1 text-neon text-xs font-bold">
                            <Crown className="w-3 h-3" /> PRO
                          </span>
                        ) : (
                          <span className="text-zinc-600 text-xs">FREE</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div>
                          <div className="font-bold text-white">{submission.artist_name}</div>
                          {submission.instagram_profile && (
                            <div className="text-zinc-500 text-xs mt-1">{submission.instagram_profile}</div>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-zinc-300">{submission.track_title}</td>
                      <td className="p-4 text-zinc-400">{submission.email}</td>
                      <td className="p-4 text-zinc-400">
                        {submission.monthly_listeners?.toLocaleString() || '-'}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <a 
                            href={submission.spotify_soundcloud_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-neon hover:text-white transition-colors duration-200"
                            data-testid={`spotify-link-${index}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          {submission.tiktok_link && (
                            <a 
                              href={submission.tiktok_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-zinc-500 hover:text-white transition-colors duration-200"
                              data-testid={`tiktok-link-${index}`}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <Select 
                          value={submission.status} 
                          onValueChange={(value) => updateStatus(submission.id, value)}
                        >
                          <SelectTrigger className="w-[130px] bg-transparent border-0 p-0 h-auto focus:ring-0 focus:ring-offset-0" data-testid={`status-select-${index}`}>
                            <StatusBadge status={submission.status} />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                            <SelectItem value="pending" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Pending</SelectItem>
                            <SelectItem value="reviewing" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Reviewing</SelectItem>
                            <SelectItem value="selected" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Selected</SelectItem>
                            <SelectItem value="rejected" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4 text-zinc-500 text-xs">
                        {formatDate(submission.created_at)}
                      </td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteSubmission(submission.id)}
                          className="text-zinc-500 hover:text-red-400 p-2 h-auto transition-colors duration-200"
                          data-testid={`delete-btn-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              )}

              {/* Traction notes section */}
              {filteredSubmissions.some(s => s.traction_note) && (
                <div className="mt-8">
                  <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-4">Traction Notes</h2>
                  <div className="space-y-4">
                    {filteredSubmissions.filter(s => s.traction_note).map((submission, index) => (
                      <div 
                        key={submission.id} 
                        className="border border-zinc-800 p-4 hover:border-zinc-700 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-4 mb-2">
                          {submission.is_pro && <Crown className="w-4 h-4 text-neon" />}
                          <span className="font-bold text-white">{submission.artist_name}</span>
                          <span className="text-zinc-500">—</span>
                          <span className="text-zinc-400">{submission.track_title}</span>
                          <StatusBadge status={submission.status} />
                        </div>
                        <p className="text-zinc-400 text-sm leading-relaxed">{submission.traction_note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : activeTab === "dj-list" ? (
            /* DJ Applications Table */
            <>
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Disc3 className="w-12 h-12 text-neon animate-spin" />
                </div>
              ) : filteredDjApplications.length === 0 ? (
                <div className="text-center py-20 border border-zinc-800">
                  <p className="text-zinc-500 text-lg">No DJ applications found</p>
                </div>
              ) : (
                <div className="border border-zinc-800 overflow-x-auto">
                  <table className="w-full text-sm" data-testid="dj-applications-table">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-zinc-900/50">
                        <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">DJ Name</th>
                        <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Full Name</th>
                        <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Email</th>
                        <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Location</th>
                        <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Genres</th>
                        <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Links</th>
                        <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Status</th>
                        <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Date</th>
                        <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-bold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDjApplications.map((application, index) => (
                        <tr 
                          key={application.id} 
                          className="border-b border-zinc-800/50 hover:bg-zinc-900/30 transition-colors duration-200"
                          data-testid={`dj-row-${index}`}
                        >
                          <td className="p-4">
                            <div className="font-bold text-white">{application.dj_name}</div>
                          </td>
                          <td className="p-4 text-zinc-400">{application.full_name}</td>
                          <td className="p-4 text-zinc-400">{application.email}</td>
                          <td className="p-4 text-zinc-500">{application.country_city || '-'}</td>
                          <td className="p-4">
                            <div className="flex flex-wrap gap-1">
                              {application.genres.slice(0, 3).map((genre, i) => (
                                <span key={i} className="text-xs bg-zinc-800 px-2 py-1 text-zinc-400">
                                  {genre}
                                </span>
                              ))}
                              {application.genres.length > 3 && (
                                <span className="text-xs text-zinc-600">+{application.genres.length - 3}</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              {application.instagram_link && (
                                <a 
                                  href={application.instagram_link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-neon hover:text-white transition-colors duration-200"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                              {application.soundcloud_link && (
                                <a 
                                  href={application.soundcloud_link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-zinc-500 hover:text-white transition-colors duration-200"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <Select 
                              value={application.status} 
                              onValueChange={(value) => updateDjStatus(application.id, value)}
                            >
                              <SelectTrigger className="w-[130px] bg-transparent border-0 p-0 h-auto focus:ring-0 focus:ring-offset-0">
                                <StatusBadge status={application.status} />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                                <SelectItem value="pending" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Pending</SelectItem>
                                <SelectItem value="approved" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Approved</SelectItem>
                                <SelectItem value="rejected" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Rejected</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-4 text-zinc-500 text-xs">
                            {formatDate(application.created_at)}
                          </td>
                          <td className="p-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteDjApplication(application.id)}
                              className="text-zinc-500 hover:text-red-400 p-2 h-auto transition-colors duration-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Where they play section */}
              {filteredDjApplications.some(a => a.where_play) && (
                <div className="mt-8">
                  <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-4">Where They Play</h2>
                  <div className="space-y-4">
                    {filteredDjApplications.filter(a => a.where_play).map((application) => (
                      <div 
                        key={application.id} 
                        className="border border-zinc-800 p-4 hover:border-zinc-700 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-4 mb-2">
                          <span className="font-bold text-white">{application.dj_name}</span>
                          <span className="text-zinc-500">—</span>
                          <span className="text-zinc-400">{application.country_city || 'Location not specified'}</span>
                          <StatusBadge status={application.status} />
                        </div>
                        <p className="text-zinc-400 text-sm leading-relaxed">{application.where_play}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Publishing Submissions Table */
            <>
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Disc3 className="w-12 h-12 text-neon animate-spin" />
                </div>
              ) : filteredPublishingSubs.length === 0 ? (
                <div className="text-center py-20 border border-zinc-800">
                  <p className="text-zinc-500 text-lg">No publishing submissions found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredPublishingSubs.map((sub, index) => (
                    <div key={sub.id} className="border border-zinc-800 p-6 hover:border-zinc-700 transition-colors" data-testid={`publishing-row-${index}`}>
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-bold text-white text-lg">{sub.artist_name}</h3>
                            <StatusBadge status={sub.status} />
                          </div>
                          <p className="text-zinc-500 text-sm">{sub.full_name} · {sub.email}</p>
                          {sub.country && <p className="text-zinc-600 text-sm">{sub.country}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Select value={sub.status} onValueChange={(v) => updatePublishingStatus(sub.id, v)}>
                            <SelectTrigger className="w-[130px] bg-transparent border border-zinc-800 rounded-none focus:ring-0 focus:ring-offset-0 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                              <SelectItem value="pending" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Pending</SelectItem>
                              <SelectItem value="reviewing" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Reviewing</SelectItem>
                              <SelectItem value="contacted" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Contacted</SelectItem>
                              <SelectItem value="rejected" className="text-zinc-300 focus:bg-zinc-800 focus:text-white rounded-none">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="sm" onClick={() => deletePublishingSub(sub.id)}
                            className="text-zinc-500 hover:text-red-400 p-2 h-auto">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Tracks</p>
                          <p className="text-white font-bold">{sub.released_tracks?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Total Streams</p>
                          <p className="text-white font-bold">{sub.total_streams?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Last 6 Months</p>
                          <p className="text-white font-bold">{sub.streams_last_six_months?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Releasing</p>
                          <p className="text-white font-bold">{sub.actively_releasing}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Genres</p>
                          <div className="flex flex-wrap gap-1">
                            {sub.main_genres?.map((g, i) => (
                              <span key={i} className="text-xs bg-zinc-800 px-2 py-1 text-zinc-400">{g}</span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Publishing Deal</p>
                          <p className="text-zinc-400">{sub.has_publishing_deal}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Owns Rights</p>
                          <p className="text-zinc-400">{sub.owns_publishing_rights}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        {sub.spotify_url && (
                          <a href={sub.spotify_url} target="_blank" rel="noopener noreferrer" className="text-neon hover:text-white flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> Spotify
                          </a>
                        )}
                        {sub.soundcloud_url && (
                          <a href={sub.soundcloud_url} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-white flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> SoundCloud
                          </a>
                        )}
                        {sub.csv_storage_path && (
                          <a href={`${API}/publishing/submissions/${sub.id}/csv`} className="text-neon hover:text-white flex items-center gap-1">
                            <Download className="w-3 h-3" /> Download CSV
                          </a>
                        )}
                        {sub.no_csv_available && <span className="text-zinc-600 text-xs">No CSV provided</span>}
                        <span className="text-zinc-600 text-xs ml-auto">{formatDate(sub.created_at)}</span>
                      </div>
                      {sub.additional_info && (
                        <div className="mt-4 border-t border-zinc-800 pt-4">
                          <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Notes</p>
                          <p className="text-zinc-400 text-sm">{sub.additional_info}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
